import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import "../css/AminiiDashboard.css";
import { SkeletonCard } from "../components/Skeleton";
import AminiiNavbar from "../components/AminiiNavbar";
import AminiiFooter from "../components/AminiiFooter";
import FreeShipping from "../components/FreeShipping";
import { getCookie, decodeJwtPayload, isTokenExpired, refreshAccessToken } from "../Utils/authHelpers";
import { getOptimizedImageUrl, getOptimizedVideoUrl } from "../Utils/upload";
import { API_BASE_URL } from "../config/constants";
import { getGuestCartCount } from "../Utils/guestCart";



function AminiiDashboard() {
  const [heroSlideIndex, setHeroSlideIndex] = useState(0);
  const [heroSlides, setHeroSlides] = useState([]);
  const heroTouchStartX = useRef(null);
  const heroAutoSlideTimer = useRef(null);
  const [startIndex, setStartIndex] = useState(0);
  const [isMobileView, setIsMobileView] = useState(false);
  const [products, setProducts] = useState([]);
  const [newProducts, setNewProducts] = useState([]);
  const [loadingNewProducts, setLoadingNewProducts] = useState(true);
  const [isNewArrivalsFallback, setIsNewArrivalsFallback] = useState(false);
  const [authError, setAuthError] = useState("");
  const [pageLoading, setPageLoading] = useState(true);
  const [mainImage, setMainImage] = useState(null);
  const [heroImageFailed, setHeroImageFailed] = useState(false);
  const [category, setCategory] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userFirstName, setUserFirstName] = useState("");
  const [cartItemCount, setCartItemCount] = useState(0);
  const [userId, setUserId] = useState(null);
  const newArrivalsRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  // Reels State
  const [reels, setReels] = useState([]);

  const fetchReels = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/reels`, { withCredentials: true });
      const list = res.data?.data || res.data || [];
      setReels(Array.isArray(list) ? list : []);
    } catch (e) {
      console.warn("No reels fetched:", e);
    }
  };

  useEffect(() => {
    fetchReels();
  }, []);

  const handleReelShopClick = (reel) => {
    if (reel.productId) {
      navigate(`/productDetails/${reel.productId}`);
    } else if (reel.categorySlug) {
      navigate(`/productList/${reel.categorySlug.replace(/^by\//, "")}`);
    } else {
      navigate("/productList");
    }
  };

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const refreshToken = async () => {
    const newToken = await refreshAccessToken();
    if (!newToken) return false;
    const payload = decodeJwtPayload(newToken);
    if (payload?.userId) {
      setIsLoggedIn(true);
      setUserId(payload.userId);
      setUserFirstName(payload.firstName || "");
      return true;
    }
    return false;
  };

  const fetchUserInfo = async (userId) => {
    try {
      const token = localStorage.getItem("token");
      if (token && isTokenExpired(token)) {
        const refreshed = await refreshToken();
        if (!refreshed) {
          setIsLoggedIn(false);
          setUserId(null);
          setUserFirstName("");
          setCartItemCount(0);
          return;
        }
      }
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(
        `${API_BASE_URL}/users/${userId}`,
        { headers, withCredentials: true }
      );
      const userData = response.data?.data || response.data;
      if (userData && userData.firstName) {
        setUserFirstName(userData.firstName);
        localStorage.setItem("userFirstName", userData.firstName);
        setAuthError("");
      }
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        setIsLoggedIn(false);
        setUserId(null);
        setUserFirstName("");
        setCartItemCount(0);
      }
      // For other errors (network, 500, etc.) keep user logged in — just can't fetch name
    }
  };

  const fetchCartItemCount = async (userId) => {
    try {
      const token = localStorage.getItem("token");
      if (token && isTokenExpired(token)) {
        const refreshed = await refreshToken();
        if (!refreshed) {
          setIsLoggedIn(false);
          setUserId(null);
          setUserFirstName("");
          setCartItemCount(0);
          return;
        }
      }
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(
        `${API_BASE_URL}/cart/user/${userId}`,
        { headers, withCredentials: true }
      );
      const cartData = response.data?.data || response.data;
      setCartItemCount(Array.isArray(cartData) ? cartData.length : 0);
      setAuthError("");
    } catch (error) {
      setCartItemCount(0);
      setAuthError("Failed to fetch cart items.");
    }
  };

  const keepAlive = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.get(`${API_BASE_URL}/ping`, {
        headers,
        withCredentials: true,
      });
    } catch (error) {
      console.error("Keep-alive failed:", error);
    }
  };

  useEffect(() => {
    const jwtCookie = getCookie("jwt");
    let token = localStorage.getItem("token") || jwtCookie;

    if (jwtCookie && !localStorage.getItem("token")) {
      localStorage.setItem("token", jwtCookie);
      token = jwtCookie;
    }

    const refreshTokenValue = localStorage.getItem("refreshToken");

    if (token) {
      if (isTokenExpired(token)) {
        if (refreshTokenValue) {
          refreshToken().then((refreshed) => {
            if (!refreshed) {
              setIsLoggedIn(false);
              setUserId(null);
              setUserFirstName("");
              setCartItemCount(0);
            } else {
              const payload = decodeJwtPayload(localStorage.getItem("token"));
              if (payload?.userId) {
                setIsLoggedIn(true);
                setUserId(payload.userId);
                setUserFirstName(payload.firstName || "");
                localStorage.setItem("isLoggedIn", "true");
                localStorage.setItem("userId", payload.userId);
                localStorage.setItem("userFirstName", payload.firstName || "");
                fetchUserInfo(payload.userId);
                fetchCartItemCount(payload.userId);
              }
            }
          });
        } else {
          // Expired token and no refresh token — logged out
          setIsLoggedIn(false);
          setUserId(null);
          setUserFirstName("");
          setCartItemCount(0);
        }
      } else {
        const payload = decodeJwtPayload(token);
        if (payload?.userId) {
          setIsLoggedIn(true);
          setUserId(payload.userId);
          setUserFirstName(payload.firstName || "");
          localStorage.setItem("isLoggedIn", "true");
          localStorage.setItem("userId", payload.userId);
          localStorage.setItem("userFirstName", payload.firstName || "");
          fetchUserInfo(payload.userId);
          fetchCartItemCount(payload.userId);
        } else {
          setIsLoggedIn(false);
          setUserId(null);
          setUserFirstName("");
          setCartItemCount(getGuestCartCount());
        }
      }
    } else {
      // No token — unauthenticated guest
      setIsLoggedIn(false);
      setUserId(null);
      setUserFirstName("");
      setCartItemCount(getGuestCartCount());
    }

    const onGuestCartUpdate = () => {
      if (!localStorage.getItem("token")) {
        setCartItemCount(getGuestCartCount());
      }
    };
    window.addEventListener("aminii-guest-cart-updated", onGuestCartUpdate);

    const refreshInterval = setInterval(() => {
      const token = localStorage.getItem("token");
      if (token && !isTokenExpired(token)) {
        const payload = decodeJwtPayload(token);
        const timeToExpiry = payload?.exp
          ? payload.exp - Math.floor(Date.now() / 1000)
          : 0;
        if (timeToExpiry < 300) {
          refreshToken();
        }
      }
    }, 300000); // 5 minutes

    const keepAliveInterval = setInterval(() => {
      if (localStorage.getItem("isLoggedIn") === "true") {
        keepAlive();
      }
    }, 600000); // 10 minutes

    return () => {
      clearInterval(refreshInterval);
      clearInterval(keepAliveInterval);
      window.removeEventListener("aminii-guest-cart-updated", onGuestCartUpdate);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  // Auto-advance hero slider every 5 seconds, resets on user interaction
  const startHeroAutoSlide = () => {
    if (heroAutoSlideTimer.current) clearInterval(heroAutoSlideTimer.current);
    heroAutoSlideTimer.current = setInterval(() => {
      setHeroSlideIndex((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
  };

  useEffect(() => {
    if (heroSlides.length <= 1) return;
    startHeroAutoSlide();
    return () => { if (heroAutoSlideTimer.current) clearInterval(heroAutoSlideTimer.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heroSlides.length]);

  const handleHeroTouchStart = (e) => {
    heroTouchStartX.current = e.touches[0].clientX;
    // Pause auto-slide while user is interacting
    if (heroAutoSlideTimer.current) clearInterval(heroAutoSlideTimer.current);
  };

  const handleHeroTouchEnd = (e) => {
    if (heroTouchStartX.current === null) return;
    const diff = heroTouchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) {
      if (diff > 0) {
        // Swipe left — next slide
        setHeroSlideIndex((prev) => (prev + 1) % heroSlides.length);
      } else {
        // Swipe right — prev slide
        setHeroSlideIndex((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
      }
    }
    heroTouchStartX.current = null;
    // Resume auto-slide after interaction
    if (heroSlides.length > 1) startHeroAutoSlide();
  };

  const handleHeroPrev = () => {
    setHeroSlideIndex((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
    if (heroSlides.length > 1) startHeroAutoSlide();
  };

  const handleHeroNext = () => {
    setHeroSlideIndex((prev) => (prev + 1) % heroSlides.length);
    if (heroSlides.length > 1) startHeroAutoSlide();
  };

  const getFirstImage = (product, width = 800) => {
    const raw =
      product?.productImages?.[0] ||
      product?.ProductImages?.[0] ||
      "";
    if (!raw) return "https://placehold.co/50x50?text=No+Image";
    return getOptimizedImageUrl(raw, { width, quality: "auto", colorSpace: "srgb" });
  };


  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target;
            img.src = img.dataset.src;
            img.classList.add("amin-image-loaded");
            observer.unobserve(img);
          }
        });
      },
      { rootMargin: "50px", threshold: 0 }
    );

    const lazyImages = document.querySelectorAll(".amin-lazy-load");
    lazyImages.forEach((img) => observer.observe(img));

    return () => {
      lazyImages.forEach((img) => observer.unobserve(img));
    };
  }, [newProducts]);

  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    const [day, month, year] = dateStr.split("-").map(Number);
    return new Date(year, month - 1, day);
  };

  const fetchAllProducts = async () => {
    try {
      setLoadingNewProducts(true);
      const token = localStorage.getItem("token");
      if (token && isTokenExpired(token)) {
        const refreshed = await refreshToken();
        if (!refreshed) {
          setIsLoggedIn(false);
          setUserId(null);
          setUserFirstName("");
          setCartItemCount(0);
        }
      }
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API_BASE_URL}/products`, {
        headers,
        withCredentials: true,
      });
      const allProducts = response.data?.data || [];

      // Helper function to check if a category name is "Main Image" / "Main-Image"
      const isMainImageCategoryName = (name) => {
        if (!name) return false;
        const normalized = name.toLowerCase().replace(/[-_\s]+/g, "");
        return normalized === "mainimage";
      };

      // --- Separate banner products from real products ---
      const isMainImage = (p) =>
        p.productCategory?.some(
          (cat) =>
            isMainImageCategoryName(cat.categoryName) ||
            isMainImageCategoryName(cat.subCategory?.categoryName)
        );

      const bannerProducts = allProducts.filter(isMainImage);
      const regularProducts = allProducts.filter((p) => !isMainImage(p));

      // Only regular products go into state — banners NEVER appear in any product section
      setProducts(regularProducts);
      setAuthError("");

      // Build hero slides: ONE slide per banner product
      const slides = bannerProducts.slice(0, 10).map((product) => {
        const images = product.productImages || product.ProductImages || [];
        const desktopImage = images[0] || "";
        const mobileImage = images[1] || images[0] || "";
        // Find the subcategory to build the click link
        const subCat = product.productCategory
          ?.flatMap((cat) => [
            cat,
            ...(cat.subCategory ? [cat.subCategory] : []),
          ])
          .find(
            (cat) =>
              cat.categoryName &&
              !isMainImageCategoryName(cat.categoryName)
          );
        const linkSlug = subCat?.categoryName
          ? subCat.categoryName.toLowerCase().replace(/\s+/g, "-")
          : null;
        // Use productName as headline; fall back if it's the generic marker name
        const isGenericName =
          !product.productName ||
          isMainImageCategoryName(product.productName);
        const headline = isGenericName
          ? "AMINII CLOTHING"
          : product.productName.toUpperCase();
        return {
          id: `hero-${product.productId}`,
          desktopImage,
          mobileImage,
          headline,
          linkSlug,
        };
      });
      setHeroSlides(slides);
      setMainImage(bannerProducts[0] || null);

      const currentDate = new Date();
      const cutoffDate = new Date();
      cutoffDate.setDate(currentDate.getDate() - 14); // 2-week cutoff as requested

      let filteredNewProducts = regularProducts
        .filter((product) => {
          const dateStr = product.productDate || product.ProductDate;
          if (!dateStr) return false;
          const productDate = parseDate(dateStr);
          return productDate && productDate >= cutoffDate;
        })
        .sort((a, b) => {
          const dateA = parseDate(a.productDate || a.ProductDate);
          const dateB = parseDate(b.productDate || b.ProductDate);
          return dateB - dateA;
        });

      // Fallback: if fewer than 4 products uploaded in last 2 weeks, show top 8 newest products overall
      if (filteredNewProducts.length < 4) {
        filteredNewProducts = [...regularProducts]
          .sort((a, b) => {
            const dateA = parseDate(a.productDate || a.ProductDate) || new Date(0);
            const dateB = parseDate(b.productDate || b.ProductDate) || new Date(0);
            return dateB - dateA;
          })
          .slice(0, 8);
        setIsNewArrivalsFallback(true);
      } else {
        setIsNewArrivalsFallback(false);
      }

      setNewProducts(filteredNewProducts);
    } catch (error) {
      setAuthError("Failed to fetch products.");
      setProducts([]);
      setNewProducts([]);
    } finally {
      setLoadingNewProducts(false);
      setPageLoading(false);
    }
  };

  useEffect(() => {
    fetchAllProducts();
  }, []);

  const getOneProductPerCategory = () => {
    if (!Array.isArray(products)) return [];
    const categoryProductMap = {};

    products.forEach((product) => {
      const categories = product?.productCategory || [];
      if (categories.length > 0) {
        const mainCategory = categories[0].categoryName?.toLowerCase();
        if (!mainCategory || mainCategory.replace(/[-_\s]+/g, "") === "mainimage") return;
        if (!categoryProductMap[mainCategory]) {
          categoryProductMap[mainCategory] = product;
        }
      }
    });

    return Object.values(categoryProductMap);
  };

  const filteredProducts = getOneProductPerCategory();


  // const handleNext = () => {
  //   if (startIndex + (isMobileView ? 1 : 1) < filteredProducts.length) {
  //     setStartIndex(startIndex + (isMobileView ? 1 : 1));
  //     scrollCarousel(1);
  //   }
  // };

  // const handlePrev = () => {
  //   if (startIndex - (isMobileView ? 1 : 1) >= 0) {
  //     setStartIndex(startIndex - (isMobileView ? 1 : 1));
  //     scrollCarousel(-1);
  //   }
  // };

  // const scrollCarousel = (direction) => {
  //   if (carouselRef.current) {
  //     const scrollAmount =
  //       direction * (carouselRef.current.offsetWidth / (isMobileView ? 2 : 4));
  //     carouselRef.current.scrollBy({
  //       left: scrollAmount,
  //       behavior: "smooth",
  //     });
  //   }
  // };

  const handleMouseDown = (e, ref) => {
    setIsDragging(true);
    setStartX(e.pageX - ref.current.offsetLeft);
    setScrollLeft(ref.current.scrollLeft);
  };

  const handleTouchStart = (e, ref) => {
    setIsDragging(true);
    setStartX(e.touches[0].pageX - ref.current.offsetLeft);
    setScrollLeft(ref.current.scrollLeft);
  };

  const handleMouseMove = (e, ref) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - ref.current.offsetLeft;
    const walk = (x - startX) * 2;
    const maxScroll = ref.current.scrollWidth - ref.current.clientWidth;
    let newScrollLeft = scrollLeft - walk;
    if (newScrollLeft < 0) newScrollLeft = 0;
    if (newScrollLeft > maxScroll) newScrollLeft = maxScroll;
    ref.current.scrollLeft = newScrollLeft;
  };

  const handleTouchMove = (e, ref) => {
    if (!isDragging) return;
    const x = e.touches[0].pageX - ref.current.offsetLeft;
    const walk = (x - startX) * 2;
    const maxScroll = ref.current.scrollWidth - ref.current.clientWidth;
    let newScrollLeft = scrollLeft - walk;
    if (newScrollLeft < 0) newScrollLeft = 0;
    if (newScrollLeft > maxScroll) newScrollLeft = maxScroll;
    ref.current.scrollLeft = newScrollLeft;
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const handleBannerClick = (slide) => {
    if (slide.linkSlug) {
      navigate(`/productList/${slide.linkSlug.replace(/^by\//, "")}`);
    } else {
      navigate("/productList");
    }
  };

  const handleShopNowClick = () => {
    navigate("/productList");
  };

  const handleLogoutCleanup = () => {
    setIsLoggedIn(false);
    setUserFirstName("");
    setUserId(null);
    setCartItemCount(0);
  };

  const handleCategoryNavigation = (product) => {
    if (product?.productCategory?.length > 0) {
      const cat = product.productCategory[0].categoryName?.toLowerCase().replace(/\s+/g, "-") || "";
      const sub = product.productCategory[0].subCategory?.categoryName?.toLowerCase().replace(/\s+/g, "-") || "";
      if (sub && sub !== cat) {
        navigate(`/productList/${cat}/${sub}`);
      } else if (cat) {
        navigate(`/productList/${cat}`);
      } else {
        navigate("/productList");
      }
    } else {
      navigate("/productList");
    }
  };

  if (pageLoading) {
    return (
      <div className="amin-page-loader">
        <div className="amin-page-loader-inner">
          <div className="amin-loader-logo">
            <span className="amin-loader-logo-a">A M I N I I</span>
            {/* <span className="amin-loader-logo-text">MINII</span> */}
          </div>
          <p className="amin-loader-sub">CLOTHING</p>
          <div className="amin-loader-bar-wrap">
            <div className="amin-loader-bar"></div>
          </div>
          <p className="amin-loader-tagline">Curating your luxury experience&hellip;</p>
        </div>
      </div>
    );
  }

  return (
    <div className="amin-dashboard">
      <AminiiNavbar
        isLoggedIn={isLoggedIn}
        userFirstName={userFirstName}
        cartItemCount={cartItemCount}
        onLogout={handleLogoutCleanup}
      />

      {/* Multi-Slide Luxury Hero Carousel - images loaded from DB */}
      <div className="amin-hero-slider-wrapper">
        {heroSlides.length > 0 ? (
        <>
            <div
              className="amin-hero-slide-container"
              style={{
                transform: `translateX(-${(heroSlideIndex || 0) * 100}%)`,
                transition: "transform 0.6s cubic-bezier(0.25, 1, 0.5, 1)",
                display: "flex",
                width: "100%",
                height: "100%",
              }}
              onTouchStart={handleHeroTouchStart}
              onTouchEnd={handleHeroTouchEnd}
            >
              {heroSlides.map((slide, idx) => (
                <div
                  key={slide.id || idx}
                  className="amin-hero-single-slide"
                  onClick={() => handleBannerClick(slide)}
                  style={{ cursor: slide.linkSlug ? "pointer" : "default" }}
                >
                  {/* Desktop image */}
                  <img
                    src={getOptimizedImageUrl(slide.desktopImage, { width: 1920, quality: "auto", colorSpace: "srgb" })}
                    alt={slide.headline}
                    className="amin-hero-img amin-hero-img-desktop"
                    fetchPriority="high"
                    decoding="async"
                  />
                  {/* Mobile image — hidden on desktop via CSS */}
                  <img
                    src={getOptimizedImageUrl(slide.mobileImage, { width: 800, quality: "auto", colorSpace: "srgb" })}
                    alt={slide.headline}
                    className="amin-hero-img amin-hero-img-mobile"
                    fetchPriority="high"
                    decoding="async"
                  />
                  {/* Overlay text */}
                  <div className="amin-hero-overlay-content">
                    {slide.headline && slide.headline !== "AMINII CLOTHING" && (
                      <p className="amin-hero-tagline">{slide.headline}</p>
                    )}
                    <button
                      className="amin-hero-cta-btn"
                      onClick={(e) => { e.stopPropagation(); handleBannerClick(slide); }}
                    >
                      SHOP NOW
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Prev / Next Arrows */}
            {heroSlides.length > 1 && (
              <>
                <button
                  className="amin-hero-arrow amin-hero-arrow-prev"
                  onClick={(e) => { e.stopPropagation(); handleHeroPrev(); }}
                  aria-label="Previous slide"
                >
                  ‹
                </button>
                <button
                  className="amin-hero-arrow amin-hero-arrow-next"
                  onClick={(e) => { e.stopPropagation(); handleHeroNext(); }}
                  aria-label="Next slide"
                >
                  ›
                </button>
              </>
            )}

            {/* Hero Slider Dots */}
            {heroSlides.length > 1 && (
              <div className="amin-hero-dots">
                {heroSlides.map((_, idx) => (
                  <button
                    key={idx}
                    className={`amin-hero-dot ${idx === (heroSlideIndex || 0) ? "active" : ""}`}
                    onClick={() => { setHeroSlideIndex(idx); if (heroSlides.length > 1) startHeroAutoSlide(); }}
                    aria-label={`Go to slide ${idx + 1}`}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="amin-hero-placeholder">
            <div className="amin-hero-placeholder-content">
              {/* <p className="amin-hero-placeholder-eyebrow">Est. 2024</p> */}
              <h1 className="amin-hero-placeholder-title">AMINII CLOTHING</h1>
              <div className="amin-hero-placeholder-divider">
                <span className="amin-hero-placeholder-line"></span>
                <span className="amin-hero-placeholder-gem">◆</span>
                <span className="amin-hero-placeholder-line"></span>
              </div>
              <p className="amin-hero-placeholder-sub">LUXURY DESIGNER WEAR</p>
              <button className="amin-hero-placeholder-btn" onClick={handleShopNowClick}>
                SHOP NOW
              </button>
            </div>
          </div>
        )}
      </div>
      {products.length > 0 ? (
        <div className="amin-category-section-container">
          <h2 className="amin-section-title">SHOP BY CATEGORY</h2>
          <div className="amin-category-grid">
            {filteredProducts.map((product, index) => {
              const rawCat = product?.productCategory?.[0]?.categoryName || "Collection";
              const catName = rawCat.toUpperCase();
              return (
                <div
                  key={index}
                  className="amin-product-item amin-category-card-item"
                  onClick={() => handleCategoryNavigation(product)}
                >
                  <div className="amin-product-img-wrap">
                    <img
                      className="productImages"
                      src={getFirstImage(product)}
                      loading="lazy"
                      decoding="async"
                      alt={catName}
                      onError={(e) => {
                        e.target.style.opacity = "0";
                        e.target.onerror = null;
                      }}
                    />
                    <div className="amin-category-center-label">
                      <span className="amin-category-center-text">{catName}</span>
                    </div>
                    <div className="amin-category-watermark-overlay amin-category-price-only">
                      <span className="amin-category-watermark-price">
                        ₹{product.productPrice || product.price || "2,000"}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : authError ? (
        <div className="amin-error-state">
          <p className="amin-error-icon">&#9888;</p>
          <p className="amin-error-title">Unable to load products</p>
          <p className="amin-error-subtitle">Please check your connection and try again.</p>
          <button className="amin-error-retry" onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      ) : (
        <div className="amin-error-state">
          <p className="amin-error-subtitle">Loading products...</p>
        </div>
      )}

      <div className="amin-new-updates">
        <h2>{isNewArrivalsFallback ? "Recently Added" : "New Arrivals"}</h2>
        {loadingNewProducts ? (
          <div className="amin-product-grid">
            {[...Array(4)].map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : newProducts.length > 0 ? (
          <div
            className="amin-product-grid"
            ref={newArrivalsRef}
            onMouseDown={(e) => handleMouseDown(e, newArrivalsRef)}
            onMouseMove={(e) => handleMouseMove(e, newArrivalsRef)}
            onMouseUp={handleDragEnd}
            onMouseLeave={handleDragEnd}
            onTouchStart={(e) => handleTouchStart(e, newArrivalsRef)}
            onTouchMove={(e) => handleTouchMove(e, newArrivalsRef)}
            onTouchEnd={handleDragEnd}
          >
            {newProducts.map((product) => (
              <div
                key={product.productId}
                className="amin-product-item amin-category-card-item"
                onClick={() => navigate(`/productDetails/${product.productId}`)}
              >
                <div className="amin-product-img-wrap">
                  <img
                    className="productImages"
                    src={getFirstImage(product)}
                    loading="lazy"
                    decoding="async"
                    alt={product.productName || product.ProductName}
                    onError={(e) => {
                      e.target.style.opacity = "0";
                      e.target.onerror = null;
                    }}
                  />
                  <div className="amin-category-watermark-overlay">
                    <span className="amin-category-watermark-text">
                      {product.productName || product.ProductName}
                    </span>
                    <span className="amin-category-watermark-price">
                      ₹{product.productPrice || product.ProductPrice}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {/* Dynamic Reels & Lookbook Section */}
      <section className="amin-reels-section" aria-label="Curated Lookbook Reels">
        <div className="amin-reels-header">
          <h2 className="amin-reels-title">LOOKBOOK & REELS</h2>
          <p className="amin-reels-sub">EXPLORE OUR LATEST DESIGNS IN MOTION</p>
        </div>

        {reels.length > 0 ? (
          <div className="amin-reels-grid">
            {reels.map((reel) => {
              const isDirectVideo =
                /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(reel.videoUrl) ||
                (typeof reel.videoUrl === "string" && reel.videoUrl.includes("/video/upload/"));
              return (
                <div key={reel.id} className="amin-reel-card">
                  <div className="amin-reel-video-wrap">
                    {isDirectVideo ? (
                      <video
                        src={getOptimizedVideoUrl(reel.videoUrl, { width: 540 })}
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="amin-reel-video"
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    ) : (
                      <div className="amin-reel-placeholder">
                        <div className="amin-reel-placeholder-icon">▶</div>
                      </div>
                    )}
                    <div className="amin-reel-overlay">
                      {reel.title && <h4 className="amin-reel-card-title">{reel.title}</h4>}
                      <div className="amin-reel-actions">
                        <a
                          href={reel.instagramUrl || "https://www.instagram.com/aminii.clothing.in"}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="amin-reel-btn amin-reel-insta-btn"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Instagram
                        </a>
                        <button
                          className="amin-reel-btn amin-reel-shop-btn"
                          onClick={() => handleReelShopClick(reel)}
                        >
                          Shop Now
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="amin-reels-empty">
            <a
              href="https://www.instagram.com/aminii.clothing.in"
              target="_blank"
              rel="noopener noreferrer"
              className="amin-insta-btn"
            >
              Follow @aminii.clothing.in on Instagram
            </a>
          </div>
        )}
      </section>

      <FreeShipping />
      <AminiiFooter />
    </div>
  );
}

export default AminiiDashboard;
